import type { ReactNode } from "react";
import { Money, Pill, cx } from "@frontend/components/ui";
import { BoostTierIcon } from "@frontend/components/boost-tier-icon";
import {
  CheckIcon,
  ClockIcon,
  CopyIcon,
  LockIcon,
  PhoneIcon,
} from "@frontend/components/site/icons";
import { splitProportionally } from "@creators/backend/lib/money";
import { TRIGGER_LABELS } from "@creators/backend/lib/collabs/schedule";

/**
 * Product stills for the marketing page.
 *
 * Built out of the same tokens and primitives as the app rather than captured as
 * screenshots: a PNG would not follow the dark theme, would blur on a retina
 * screen, and would quietly go stale the first time the collab room moves a
 * border. Every still is decorative — the section around it makes the same claim
 * in prose, so they are hidden from assistive tech instead of being given alt
 * text that would read as a wall of numbers.
 */

/** One deal, shown consistently across every still on the page. */
const TOTAL = 8_500_000;
const SCHEDULE = [25, 25, 50];
const TRANCHES = splitProportionally(TOTAL, SCHEDULE);
const TRIGGERS = ["on_accept", "on_draft_approval", "on_publish"] as const;

function Frame({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      aria-hidden="true"
      className={cx(
        // Force ink text — stills sit inside dark marketing bands that inherit
        // `text-canvas`, which would wash amounts out on the white surface.
        "overflow-hidden rounded-2xl border border-line bg-surface text-ink shadow-[0_24px_60px_-28px_rgb(11_13_18/0.35)] select-none",
        className,
      )}
    >
      <div className="flex items-center gap-2.5 border-b border-line bg-canvas/60 px-3.5 py-2.5">
        <span className="flex shrink-0 gap-1">
          <span className="size-2 rounded-full bg-line-strong" />
          <span className="size-2 rounded-full bg-line-strong" />
          <span className="size-2 rounded-full bg-line-strong" />
        </span>
        <span className="truncate font-mono text-[11px] text-ink-subtle">{label}</span>
      </div>
      {children}
    </div>
  );
}

function Chip({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-md bg-warn-50 px-2 py-0.5 text-[11px] text-warn-700 ring-1 ring-warn-100">
      {children}
    </span>
  );
}

/** The hero still: a countered offer, its diff, and the clock on it. */
export function CollabRoomStill({ host }: { host: string }) {
  return (
    <Frame label={`${host}/collabs/DL-4417`}>
      <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-canvas text-[10px] font-semibold text-ink-muted ring-1 ring-line">
            AS
          </span>
          <div className="min-w-0">
            <p className="truncate text-[13px] font-semibold text-ink">Acme Skin Co.</p>
            <p className="truncate text-[11px] text-ink-subtle">DL-4417 · v2 of the terms</p>
          </div>
        </div>
        <Pill tone="amber">Negotiating</Pill>
      </div>

      <div className="p-4">
        <div className="card overflow-hidden">
          <div className="flex items-center justify-between gap-3 border-b border-line px-3.5 py-2.5">
            <p className="text-[13px] font-semibold text-ink">Collab terms</p>
            <span className="rounded bg-canvas px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-ink-subtle uppercase">
              Countered
            </span>
          </div>

          <div className="px-3.5 py-3">
            <Money paise={TOTAL} size="lg" className="tracking-tight text-ink" />
            <p className="mt-1 text-[13px] leading-snug text-ink-muted">
              One 60s reel plus three stories, two revisions included.
            </p>

            <ul className="mt-3 flex flex-wrap gap-1.5">
              <li>
                <Chip>
                  <span className="font-medium">amount</span> ₹60,000 → ₹85,000
                </Chip>
              </li>
              <li>
                <Chip>
                  <span className="font-medium">deliver by</span> 8 Aug → 14 Aug
                </Chip>
              </li>
            </ul>

            <dl className="mt-3 space-y-1.5 border-t border-line pt-3 text-[13px]">
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-ink-muted">Deliver by</dt>
                <dd className="tabular font-medium text-ink">14 Aug 2026</dd>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-ink-muted">Compensation</dt>
                <dd className="font-medium text-ink">Paid directly</dd>
              </div>
            </dl>

            <ul className="mt-3 space-y-1.5 border-t border-line pt-2.5 text-[11px]">
              {TRIGGERS.map((trigger, index) => (
                <li key={trigger} className="flex justify-between gap-2">
                  <span className="text-ink-muted">{TRIGGER_LABELS[trigger]}</span>
                  <span className="tabular font-medium text-ink">
                    {SCHEDULE[index]}% · <Money paise={TRANCHES[index]} size="sm" />
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-line bg-canvas/60 px-4 py-2.5">
        <p className="tabular flex items-center gap-1.5 text-[11px] font-medium text-ink-muted">
          <ClockIcon className="size-3.5" />
          Offer expires in 41h 06m
        </p>
        <span className="rounded-lg bg-brand-600 px-2.5 py-1 text-[11px] font-medium text-canvas">
          Accept terms
        </span>
      </div>
    </Frame>
  );
}

/** Every state change, written into the thread as it happens. */
export function ThreadStill() {
  // `timer` marks the entries the platform wrote on its own, which is the point
  // of showing a thread at all — nobody sent that message.
  const events: { at: string; body: string; actor: string; tone?: "money" | "timer" }[] = [
    { at: "2 Aug", body: "Offer sent — ₹60,000, deliver by 8 Aug", actor: "Acme Skin Co." },
    { at: "3 Aug", body: "Countered — ₹85,000, deliver by 14 Aug", actor: "Aanya" },
    { at: "3 Aug", body: "Terms accepted. Contract frozen at v2.", actor: "Acme Skin Co." },
    { at: "4 Aug", body: "Advance of ₹21,250 marked paid by UPI", actor: "Acme Skin Co." },
    { at: "4 Aug", body: "Advance confirmed received", actor: "Aanya", tone: "money" },
    {
      at: "9 Aug",
      body: "Draft auto-approved — review window elapsed",
      actor: "Send Collab",
      tone: "timer",
    },
  ];

  return (
    <Frame label="Thread · DL-4417">
      <ol className="divide-y divide-line/60">
        {events.map((event) => (
          <li key={event.body} className="flex gap-3 px-4 py-2.5">
            <span
              className={cx(
                "mt-1 flex size-5 shrink-0 items-center justify-center rounded-full ring-1",
                event.tone === "timer" && "bg-warn-50 text-warn-700 ring-warn-100",
                event.tone === "money" && "bg-money-50 text-money-700 ring-money-100",
                !event.tone && "bg-canvas text-ink-subtle ring-line",
              )}
            >
              {event.tone === "timer" ? (
                <ClockIcon className="size-3" />
              ) : (
                <CheckIcon className="size-3" />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] leading-snug text-ink">{event.body}</p>
              <p className="mt-0.5 text-[11px] text-ink-subtle">
                {event.actor} · {event.at}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </Frame>
  );
}

/** The five clocks, and what each one does when it runs out. */
export function TimerStill() {
  const timers = [
    { name: "Offer expiry", effect: "Offer lapses", left: "41h 06m", live: true },
    { name: "Advance window", effect: "Terms lapse, no work starts", left: "—" },
    { name: "Draft review", effect: "Draft auto-approves", left: "3d 00h" },
    { name: "Publish confirmation", effect: "Post auto-confirms live", left: "—" },
    { name: "Live period", effect: "Final tranche falls due", left: "—" },
  ];

  return (
    <Frame label="Timers on this collab">
      <ul className="divide-y divide-line/60">
        {timers.map((timer) => (
          <li key={timer.name} className="flex items-center gap-3 px-4 py-2.5">
            <span
              className={cx(
                "flex size-6 shrink-0 items-center justify-center rounded-lg ring-1",
                timer.live
                  ? "bg-warn-50 text-warn-700 ring-warn-100"
                  : "bg-canvas text-ink-subtle ring-line",
              )}
            >
              <ClockIcon className="size-3.5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium text-ink">{timer.name}</p>
              <p className="truncate text-[11px] text-ink-muted">{timer.effect}</p>
            </div>
            <span
              className={cx(
                "tabular shrink-0 text-[11px] font-semibold",
                timer.live ? "text-warn-700" : "text-ink-subtle",
              )}
            >
              {timer.left}
            </span>
          </li>
        ))}
      </ul>
      <p className="border-t border-line bg-canvas/60 px-4 py-2 text-[11px] leading-snug text-ink-subtle">
        Both sides see the same countdown, everywhere the collab appears.
      </p>
    </Frame>
  );
}

/** Tranches walking pending → due → paid → confirmed, with the proof attached. */
export function LedgerStill() {
  const rows = [
    { label: TRIGGER_LABELS.on_accept, amount: TRANCHES[0], state: "Confirmed" as const },
    { label: TRIGGER_LABELS.on_draft_approval, amount: TRANCHES[1], state: "Paid" as const },
    { label: TRIGGER_LABELS.on_publish, amount: TRANCHES[2], state: "Pending" as const },
  ];

  const tones = {
    Confirmed: "bg-money-50 text-money-700 border-money-100",
    Paid: "bg-warn-50 text-warn-700 border-warn-100",
    Pending: "bg-canvas text-ink-muted border-line",
  };

  return (
    <Frame label="Payments · DL-4417">
      <div className="flex items-baseline justify-between gap-3 border-b border-line px-4 py-3">
        <div>
          <p className="text-[11px] font-medium tracking-wide text-ink-subtle uppercase">
            Confirmed received
          </p>
          <Money paise={TRANCHES[0]} size="lg" className="mt-1 block tracking-tight" />
        </div>
        <p className="tabular text-[11px] text-ink-muted">
          of <Money paise={TOTAL} size="sm" className="font-medium" />
        </p>
      </div>

      <ul className="divide-y divide-line/60">
        {rows.map((row) => (
          <li key={row.label} className="flex items-center gap-3 px-4 py-2.5">
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium text-ink">{row.label}</p>
              <p className="tabular mt-0.5 text-[11px] text-ink-muted">
                <Money paise={row.amount} size="sm" className="font-medium" />
              </p>
            </div>
            <span
              className={cx(
                "shrink-0 rounded-md border px-2 py-0.5 text-[11px] font-medium",
                tones[row.state],
              )}
            >
              {row.state}
            </span>
          </li>
        ))}
      </ul>

      <div className="flex items-center gap-2.5 border-t border-line bg-canvas/60 px-4 py-2.5">
        <span className="flex size-6 shrink-0 items-center justify-center rounded-lg bg-surface text-ink-subtle ring-1 ring-line">
          <LockIcon className="size-3.5" />
        </span>
        <p className="min-w-0 flex-1 text-[11px] leading-snug text-ink-muted">
          Transfer screenshot · UPI ref 4419<span className="text-ink-subtle">…</span> · visible to
          both parties only
        </p>
      </div>
    </Frame>
  );
}

/** The signed-in view: what is owed, what is moving, what is done. */
export function DashboardStill() {
  const collabs = [
    { name: "Acme Skin Co.", amount: TOTAL, status: "Negotiating", tone: "amber" as const },
    { name: "Hosa Coffee", amount: 4_000_000, status: "Awaiting payment", tone: "amber" as const },
    { name: "Bloom Studio", amount: 12_500_000, status: "In progress", tone: "blue" as const },
    { name: "Nira Wear", amount: 6_000_000, status: "Completed", tone: "green" as const },
  ];

  return (
    <Frame label="Your collabs">
      <div className="grid grid-cols-2 gap-2.5 p-3.5">
        <div className="rounded-xl border border-ink/10 bg-ink p-3 text-canvas">
          <p className="text-[10px] font-medium tracking-wide text-canvas/60 uppercase">
            Confirmed this quarter
          </p>
          <p className="tabular mt-1.5 text-lg font-semibold tracking-tight">₹4,12,500</p>
        </div>
        <div className="rounded-xl border border-line bg-surface p-3">
          <p className="text-[10px] font-medium tracking-wide text-ink-subtle uppercase">
            Awaiting confirmation
          </p>
          <p className="tabular mt-1.5 text-lg font-semibold tracking-tight text-ink">₹61,250</p>
        </div>
      </div>

      <ul className="divide-y divide-line/60 border-t border-line">
        {collabs.map((collab) => (
          <li key={collab.name} className="flex items-center gap-3 px-4 py-2.5">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-canvas text-[9px] font-semibold text-ink-muted ring-1 ring-line">
              {collab.name.slice(0, 1)}
            </span>
            <p className="min-w-0 flex-1 truncate text-[13px] font-medium text-ink">
              {collab.name}
            </p>
            <span className="tabular shrink-0 text-[11px] font-semibold text-ink">
              <Money paise={collab.amount} size="sm" />
            </span>
            <Pill tone={collab.tone} className="shrink-0">
              {collab.status}
            </Pill>
          </li>
        ))}
      </ul>
    </Frame>
  );
}

/** A Priority collab's call: bridged numbers, the quota, and the recording. */
export function CallStill() {
  return (
    <Frame label="Call · DL-4417">
      <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-canvas text-[10px] font-semibold text-ink-muted ring-1 ring-line">
            AS
          </span>
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-[13px] font-semibold text-ink">
              Acme Skin Co.
              <BoostTierIcon tier="priority" />
            </p>
            <p className="truncate text-[11px] text-ink-subtle">Call approved · 8m 48s left</p>
          </div>
        </div>
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-line-strong bg-surface text-ink">
          <PhoneIcon className="size-4" />
        </span>
      </div>

      <ol className="divide-y divide-line/60">
        {[
          { body: "Call option available on this collab", at: "3 Aug" },
          { body: "Call ended (6m 12s)", at: "3 Aug" },
        ].map((event) => (
          <li key={event.body} className="flex gap-3 px-4 py-2.5">
            <span className="mt-1 flex size-5 shrink-0 items-center justify-center rounded-full bg-canvas text-ink-subtle ring-1 ring-line">
              <PhoneIcon className="size-3" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] leading-snug text-ink">{event.body}</p>
              <p className="mt-0.5 text-[11px] text-ink-subtle">Send Collab · {event.at}</p>
            </div>
          </li>
        ))}
      </ol>

      <div className="flex items-center gap-2.5 border-t border-line bg-canvas/60 px-4 py-2.5">
        <span className="flex size-6 shrink-0 items-center justify-center rounded-lg bg-surface text-ink-subtle ring-1 ring-line">
          <PhoneIcon className="size-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium text-ink">Call recording (6 min)</p>
          <span className="mt-1 flex h-1 w-full overflow-hidden rounded-full bg-line">
            <span className="h-full w-1/3 rounded-full bg-ink-subtle" />
          </span>
        </div>
      </div>
    </Frame>
  );
}

/** The one link a creator puts in their bio. */
export function LinkStill({ host }: { host: string }) {
  return (
    <div
      aria-hidden="true"
      className="flex items-center gap-2 rounded-xl border border-line bg-surface p-1.5 pl-3 shadow-sm shadow-ink/5"
    >
      <span className="flex size-1.5 shrink-0 rounded-full bg-money-600" />
      <p className="min-w-0 flex-1 truncate font-mono text-[13px] text-ink">
        <span className="text-ink-subtle">{host}/</span>aanya
      </p>
      <span className="flex shrink-0 items-center gap-1.5 rounded-lg border border-line-strong bg-surface px-2.5 py-1.5 text-[11px] font-medium text-ink-muted">
        <CopyIcon className="size-3.5" />
        Copy
      </span>
    </div>
  );
}
